import { useEffect, useState } from 'react'
import { CloudOff, RefreshCw, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { listOfflineOrders, type OfflineOrder } from '@/lib/offline/offlineStore'

/**
 * The cashier's view of orders held on this device (F-FE-N30).
 *
 * Additive only. Nothing here can cancel, void or reprice a held order — the
 * goods have already left the shop, so the local queue must never be a way to make
 * it disappear. What a pending row DOES support is the one thing service needs
 * during an outage: tapping it to load the order back into the rail and add the
 * customer's next round to it (F-FE-N31). Without that, every later round became
 * a separate order and one customer's bill arrived in pieces.
 *
 * Unreconciled rows stay inert. Those were sold offline but could not be posted, and
 * appending to work a manager has to write off would only grow the problem.
 *
 * Polls rather than subscribes: the store is local IndexedDB with no change
 * events, and a few seconds of staleness costs nothing here.
 */
export function OfflineOrdersTray({
  refreshKey,
  selectedOpId,
  onSelect,
}: {
  refreshKey?: number
  /** The held order currently loaded in the rail, if any. */
  selectedOpId?: string | null
  /** Omit to render the tray read-only (e.g. where there is no rail to load into). */
  onSelect?: (o: OfflineOrder) => void
}) {
  const [orders, setOrders] = useState<OfflineOrder[]>([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const all = await listOfflineOrders()
      if (!cancelled) setOrders(all.filter((o) => o.status !== 'synced'))
    }
    void load()
    const t = window.setInterval(load, 5000)
    return () => {
      cancelled = true
      window.clearInterval(t)
    }
  }, [refreshKey])

  if (orders.length === 0) return null

  const pending = orders.filter((o) => o.status === 'pending')
  const stuck = orders.filter((o) => o.status === 'unreconciled')

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
        <CloudOff className="h-4 w-4 shrink-0" />
        Held on this device ({orders.length})
      </div>

      <ul className="space-y-1">
        {[...pending, ...stuck].map((o) => {
          const selectable = o.status === 'pending' && onSelect != null
          const selected = selectable && selectedOpId === o.opId
          const body = (
            <>
              <span className="min-w-0 truncate">
                <span className="font-mono">#{o.orderNumber}</span>
                <span className="text-muted-foreground">
                  {' '}
                  — {o.lines.length} item{o.lines.length === 1 ? '' : 's'}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                <span className="text-xs text-muted-foreground tabular-nums">
                  {new Date(o.capturedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                {o.status === 'pending' ? (
                  <Badge variant="secondary" className="gap-1">
                    <RefreshCw className="h-3 w-3" />
                    Pending sync
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Needs a manager
                  </Badge>
                )}
              </span>
            </>
          )
          return (
            <li key={o.opId}>
              {selectable ? (
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onSelect(o)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-md px-2 py-1 text-left text-sm transition-colors',
                    selected
                      ? 'bg-amber-200/70 ring-1 ring-amber-500 dark:bg-amber-900/50'
                      : 'hover:bg-amber-200/40 dark:hover:bg-amber-900/30',
                  )}
                >
                  {body}
                </button>
              ) : (
                <div className="flex items-center justify-between gap-3 px-2 py-1 text-sm">
                  {body}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {pending.length > 0 && onSelect != null && (
        <p className="mt-2 text-xs text-amber-900/80 dark:text-amber-200/80">
          Tap a held sale to add more items to it.
        </p>
      )}

      {stuck.length > 0 && (
        <p className="mt-2 text-xs text-amber-900 dark:text-amber-200">
          {stuck.length} sale{stuck.length === 1 ? ' was' : 's were'} rung offline but could
          not be posted. A manager must resolve {stuck.length === 1 ? 'it' : 'them'}{' '}
          in Day Close before the day can be closed.
        </p>
      )}
    </div>
  )
}
